import { wsServerMessageSchema, type WsServerMessage } from "@agentdash/shared";

// Same-origin default: derive ws[s] URL from window.location so the prod
// SPA always talks to its host. Override with VITE_WS_URL in dev.
function defaultWsUrl(): string {
  if (typeof window === "undefined") return "";
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/api/live`;
}

const WS_URL: string =
  (import.meta.env.VITE_WS_URL as string | undefined) || defaultWsUrl();

export type WsConnectionState = "connecting" | "open" | "closed";

// How long a socket must stay up before it has earned the backoff reset.
// Resetting on `open` alone means a socket that opens and closes right away
// (a proxy hangup, a gateway close, an API crash loop mid-rollout) retries at
// the floor delay forever; only a connection that lasted counts as proof.
const STABLE_CONNECTION_MS = 10_000;
const MIN_BACKOFF_MS = 500;

// Client-side liveness. The server's heartbeat is a protocol-level ping the
// browser answers on its own and never surfaces to JS, and the SPA otherwise
// never writes to the socket — so when the path dies without a FIN (laptop
// sleep, Wi-Fi switch, a NAT or proxy idle drop) nothing is ever sent, nothing
// is ever retransmitted, and the browser keeps reporting "open" indefinitely
// while the Live view goes stale. Send an app-level `ping` (the gateway
// answers `pong`) and drop the socket if a ping goes unanswered — no frame
// of any kind — for the timeout; the reconnect's snapshot then resyncs the
// cache. The deadline runs from the ping, not from the last frame: a hidden
// tab's timers can be throttled to once a minute, and measuring frame age at
// the tick would then drop a healthy socket on every tick.
const PING_INTERVAL_MS = 15_000;
const LIVENESS_TIMEOUT_MS = 30_000;

export interface WsHandle {
  close(): void;
  send(msg: { type: string }): void;
}

export function connectLive(handlers: {
  onMessage(msg: WsServerMessage): void;
  onStateChange(state: WsConnectionState): void;
}): WsHandle {
  let socket: WebSocket | null = null;
  let closed = false;
  let backoffMs = MIN_BACKOFF_MS;
  let reconnectTimer: number | null = null;
  let openedAt: number | null = null;
  let pingSentAt: number | null = null;
  let watchdog: number | null = null;

  function stopWatchdog(): void {
    if (watchdog !== null) {
      window.clearInterval(watchdog);
      watchdog = null;
    }
  }

  // The one teardown path for a socket, reached from its `close` event or
  // from the watchdog giving up on it. Only the current socket settles: once
  // the watchdog has moved on, the dead socket's late `close` is ignored, so
  // it can't schedule a second reconnect or flip the state of its successor.
  function settle(ws: WebSocket): void {
    if (socket !== ws) return;
    socket = null;
    stopWatchdog();
    if (openedAt !== null && Date.now() - openedAt >= STABLE_CONNECTION_MS) {
      backoffMs = MIN_BACKOFF_MS;
    }
    openedAt = null;
    handlers.onStateChange("closed");
    if (!closed) scheduleReconnect();
  }

  function open(): void {
    if (closed) return;
    handlers.onStateChange("connecting");
    const ws = new WebSocket(WS_URL);
    socket = ws;
    ws.addEventListener("open", () => {
      if (socket !== ws) return;
      openedAt = Date.now();
      pingSentAt = null;
      handlers.onStateChange("open");
      watchdog = window.setInterval(() => {
        if (pingSentAt === null) {
          pingSentAt = Date.now();
          ws.send(JSON.stringify({ type: "ping" }));
          return;
        }
        if (Date.now() - pingSentAt >= LIVENESS_TIMEOUT_MS) {
          settle(ws);
          try {
            ws.close();
          } catch {
            // ignore
          }
        }
      }, PING_INTERVAL_MS);
    });
    ws.addEventListener("message", (ev) => {
      if (socket !== ws) return;
      pingSentAt = null;
      let raw: unknown;
      try {
        raw = JSON.parse(ev.data as string);
      } catch {
        return; // ignore non-JSON frames
      }
      // Validate against the server-message contract before it reaches the
      // store — a `snapshot` frame is written straight into the query cache.
      const parsed = wsServerMessageSchema.safeParse(raw);
      if (parsed.success) handlers.onMessage(parsed.data);
      // else: drop frames that don't match the contract
    });
    ws.addEventListener("close", () => settle(ws));
    ws.addEventListener("error", () => {
      try {
        ws.close();
      } catch {
        // ignore
      }
    });
  }

  function scheduleReconnect(): void {
    if (closed || reconnectTimer !== null) return;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      backoffMs = Math.min(backoffMs * 2, 15_000);
      open();
    }, backoffMs);
  }

  open();

  return {
    close() {
      closed = true;
      stopWatchdog();
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      socket?.close();
    },
    send(msg) {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(msg));
      }
    },
  };
}
